<template>
    <div class="file-viewer-docx tw:flex tw:flex-col tw:h-full tw:min-h-0" data-test="file-viewer-docx">
        <file-viewer-chrome :items="contextItems">
            <div
                v-if="!loading && !error"
                class="tw:flex tw:flex-wrap tw:items-center tw:gap-2"
                data-test="file-viewer-docx-controls"
            >
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-docx-zoom-out"
                    aria-label="Zoom out"
                    :disabled="zoom <= minZoom"
                    @click="zoomOut"
                >
                    <i class="uil uil-minus" aria-hidden="true" />
                </button>
                <span class="tw:text-sm tw:tabular-nums" data-test="file-viewer-docx-zoom" aria-live="polite">
                    {{ Math.round(zoom * 100) }}%
                </span>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-docx-zoom-in"
                    aria-label="Zoom in"
                    :disabled="zoom >= maxZoom"
                    @click="zoomIn"
                >
                    <i class="uil uil-plus" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-docx-fit-width"
                    aria-label="Fit to width"
                    @click="resetZoom"
                >
                    <i class="uil uil-expand-arrows-alt" aria-hidden="true" />
                </button>
            </div>
        </file-viewer-chrome>

        <div
            v-if="loading"
            class="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:text-gray-500"
            data-test="file-viewer-docx-loading"
        >
            Loading document…
        </div>
        <div
            v-else-if="error"
            class="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:text-center tw:px-4"
            data-test="file-viewer-docx-error"
        >
            <p class="tw:text-gray-700">Could not render document.</p>
        </div>
        <div
            v-else
            class="tw:flex-1 tw:min-h-0 tw:overflow-auto tw:p-4 tw:bg-gray-50"
            data-test="file-viewer-docx-viewport"
        >
            <div class="tw:origin-top-left" :style="contentStyle" data-test="file-viewer-docx-scale">
                <div ref="host" class="file-viewer-docx__host" data-test="file-viewer-docx-content" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import FileViewerChrome from '@/components/file-viewer/FileViewerChrome.vue';
import { sanitizeDocxPreviewHost } from '@/utilities/sanitizeDocxPreviewHost.js';

const props = defineProps({
    src: {
        type: String,
        required: true,
    },
    contextItems: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['ready', 'error']);

const loading = ref(true);
const error = ref(false);
const host = ref(null);
const minZoom = 0.5;
const maxZoom = 3;
const zoomStep = 0.25;
const zoom = ref(1);

let loadId = 0;

const contentStyle = computed(() => ({
    transform: `scale(${zoom.value})`,
    width: `${100 / zoom.value}%`,
}));

function zoomIn() {
    zoom.value = Math.min(maxZoom, Number((zoom.value + zoomStep).toFixed(2)));
}

function zoomOut() {
    zoom.value = Math.max(minZoom, Number((zoom.value - zoomStep).toFixed(2)));
}

function resetZoom() {
    zoom.value = 1;
}

function clearHost() {
    if (host.value) {
        host.value.replaceChildren();
    }
}

/**
 * @param {string} url
 */
async function loadDoc(url) {
    const id = ++loadId;
    loading.value = true;
    error.value = false;
    zoom.value = 1;
    clearHost();

    try {
        const resp = await fetch(url, { credentials: 'same-origin' });
        if (id !== loadId) {
            return;
        }
        if (!resp.ok) {
            throw new Error(`Failed to fetch document (${resp.status})`);
        }

        const arrayBuffer = await resp.arrayBuffer();
        if (id !== loadId) {
            return;
        }

        loading.value = false;
        await nextTick();
        if (id !== loadId || !host.value) {
            return;
        }

        const { renderAsync } = await import('docx-preview');
        if (id !== loadId || !host.value) {
            return;
        }

        await renderAsync(arrayBuffer, host.value, undefined, {
            className: 'edverum-docx',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            breakPages: false,
            renderHeaders: true,
            renderFooters: true,
            useBase64URL: true,
        });

        if (id !== loadId || !host.value) {
            return;
        }

        // docx-preview can preserve javascript: hyperlinks; strip them before display.
        sanitizeDocxPreviewHost(host.value);

        if (id !== loadId) {
            return;
        }

        emit('ready');
    } catch (e) {
        if (id !== loadId) {
            return;
        }
        error.value = true;
        loading.value = false;
        emit('error', e);
    }
}

watch(
    () => props.src,
    value => {
        if (value) {
            loadDoc(value);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    loadId += 1;
    clearHost();
});
</script>

<style scoped>
.file-viewer-docx__host {
    min-height: 100%;
}

.file-viewer-docx__host :deep(.edverum-docx-wrapper) {
    background: transparent;
    padding: 0;
}

.file-viewer-docx__host :deep(.edverum-docx) {
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    margin: 0 auto 1rem;
}
</style>
